import React, { useEffect, useState, useMemo } from 'react';
import { Search, Users, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

export default function Gananatya() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/fetch_all_category', {
        headers: {
          'Client-ID': 'lokanatya_client',
          'Client-Secret': 'super_secret_key_123',
          'Accept': 'application/json'
        }
      });
      const data = await response.json();
      setGroups(data.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGroupClick = (groupId) => {
    navigate(`/gananatya/${groupId}`);
  };

  const filteredGroups = useMemo(() => {
    return groups.filter(group =>
      group.cat_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [groups, searchQuery]);

  return (
    <div className="bg-[#fcfcfc] min-h-screen">

      {/* Hero Banner with Odia Gananatya Stage Background */}
      <div className="relative w-full h-[320px] sm:h-[380px] md:h-[420px] bg-[#dfc699] overflow-hidden flex items-center shadow-lg border-b border-amber-950/20">
        {/* Background Image */}
        <div className="absolute inset-0 z-0 flex justify-end">
          <img
            src="images/gananatya_banner_bg.jpg"
            alt="Traditional Odia Stage"
            className="w-full h-full object-cover object-right-top md:object-right filter contrast-105 brightness-100"
          />
          {/* Smooth gradient transition from solid parchment left to transparent right */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#dfc699] via-[#dfc699]/90 to-transparent w-full md:w-1/2 z-10 pointer-events-none"></div>
          <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-[#dfc699] to-transparent z-10 pointer-events-none"></div>
        </div>

        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 w-full relative z-20 flex items-center justify-between">
          <div className="flex-1 text-gray-900 max-w-lg py-4 sm:py-6 px-1 sm:px-0">
            <span className="inline-flex items-center gap-1.5 bg-amber-900/10 border border-amber-900/30 text-amber-900 font-extrabold text-[10px] sm:text-xs tracking-widest uppercase px-3 py-1 rounded-full mb-2.5 backdrop-blur-md">
              🎭 TRADITIONAL ODIA THEATRE
            </span>
            <h1 className="text-2xl sm:text-4xl md:text-6xl font-black text-amber-950 tracking-tight mb-2 sm:mb-3 drop-shadow-sm">
              Gananatya
            </h1>
            <p className="text-amber-900/90 text-xs sm:text-sm md:text-base font-semibold max-w-md mb-4 sm:mb-6 leading-relaxed">
              Discover legendary performance groups bringing traditional culture to life.
            </p>

            {/* Sleek Glassmorphism Search Bar */}
            <div className="relative max-w-md">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search gananatya groups..."
                className="w-full bg-white/80 hover:bg-white/90 focus:bg-white backdrop-blur-md border border-amber-900/20 text-gray-900 rounded-full py-2.5 sm:py-3.5 pl-10 pr-10 text-xs sm:text-sm font-semibold outline-none focus:ring-2 focus:ring-amber-800/50 transition-all placeholder:text-gray-500 shadow-lg"
              />
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-amber-900">
                <Search size={16} />
              </div>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-amber-900 hover:text-amber-950 text-xs font-bold bg-amber-900/10 hover:bg-amber-900/20 w-5 h-5 rounded-full flex items-center justify-center transition-colors"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-5 sm:py-8">

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-3 border-amber-800 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="text-4xl mb-3 opacity-50">🎭</div>
            <h3 className="text-base font-bold text-gray-700">No gananatya groups found</h3>
            <p className="text-xs text-gray-400 mt-1">Try adjusting your search query.</p>
          </div>
        ) : (
          <motion.div
            variants={{
              hidden: { opacity: 0 },
              show: { opacity: 1, transition: { staggerChildren: 0.08 } }
            }}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5 sm:gap-4 md:gap-5"
          >
            {filteredGroups.map((group) => (
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 15 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } }
                }}
                key={group.id}
                onClick={() => handleGroupClick(group.id)}
                className="bg-white rounded-2xl shadow-2xs border border-gray-100 overflow-hidden flex flex-col group cursor-pointer hover:shadow-md hover:-translate-y-1 transition-all duration-300 relative"
              >
                {/* Card Poster Image */}
                <div className="relative h-36 sm:h-44 flex items-center justify-center bg-gray-100 overflow-hidden">
                  <img
                    src={group.cat_image || "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg"}
                    alt={group.cat_name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg";
                    }}
                  />

                  <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-md border border-gray-100 text-red-600 p-1.5 rounded-xl shadow-2xs z-10 group-hover:bg-red-600 group-hover:text-white transition-colors">
                    <Users size={13} />
                  </div>
                </div>

                {/* Card Content Body */}
                <div className="p-2.5 sm:p-3.5 bg-white flex-1 flex flex-col justify-between">
                  <h3 className="text-xs sm:text-sm font-bold text-gray-900 leading-snug group-hover:text-red-600 transition-colors line-clamp-2" title={group.cat_name}>
                    {group.cat_name}
                  </h3>
                  <div className="mt-2 pt-2 border-t border-gray-50 flex items-center justify-between text-[10px] sm:text-xs font-semibold text-gray-400 group-hover:text-red-600 transition-colors">
                    <span>View Shows</span>
                    <ArrowRight size={12} className="transform group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Footer Loader Text */}
        <div className="mt-12 text-center text-gray-400 text-[10px] sm:text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2">
          <span className="opacity-50">✨</span> All performance groups loaded
        </div>

      </div>
    </div>
  );
}
